import React from 'react';
import styled from 'styled-components';
import PropTypes from 'prop-types';

import moment from 'moment';

import { Flex, Box, Label, Text } from 'rebass';

import { DotButton } from 'components';
import { GuestSaveStatus, GuestListEntryExpanded, GuestInput, GuestBirthdayInput, GuestDropdown, GuestCabin, T } from 'containers';
import { AppText } from 'domain/appText';

const StyleDiv = styled.div`

  .number-holder{
    align-self: stretch;
    align-items: center;
    justify-content: center;
    position: relative;
    min-height: 25px;
    display: flex;
  }

  .ok .number-holder{
    border-left: 5px solid #2dcb70;
  }

  .error .number-holder{
    border-left: 5px solid #e40623;
  }

  .required-fields-empty {
    display: flex;
    align-items:center;
    justify-content:center;
    position: absolute;
    top: 0;
    height: 100%;
    transition:left 0.5s, width 0.5s;
    -webkit-transition:left 0.5s, width 0.5s;
    background-color:#e40623;
  }

  .required-fields-complete {
    display: flex;
    align-items:center;
    justify-content:center;
    position: absolute;
    top: 0;
    height: 100%;
    background-color:#2dcb70;
    transition:left 0.5s, width 0.5s;
    -webkit-transition:left 0.5s, width 0.5s;
  }

  .initial {
    left: 0px;
    width: 5px;
    transition:left 0.5s, width 0.5s;
    -webkit-transition:left 0.5s, width 0.5s;
  }

  .error {
    left: -45px;
    width: 50px;
    transition:left 0.5s, width 0.5s;
    -webkit-transition:left 0.5s, width 0.5s;
  }

  .pending {
    left: -45px;
    width: 50px;
    transition:left 0.5s, width 0.5s;
    -webkit-transition:left 0.5s, width 0.5s;
  }

  .success {
    left: -45px;
    width: 50px;
    -moz-animation: successAnimation 0.5s ease-in 3s forwards;
    -webkit-animation: successAnimation 0.5s ease-in 3s forwards;
    -o-animation: successAnimation 0.5s ease-in 3s forwards;
    animation: successAnimation 0.5s ease-in 3s forwards;
    -webkit-animation-fill-mode: forwards;
    animation-fill-mode: forwards;
  }

  @keyframes successAnimation {
    to {
        left: 0px;
        width: 5px;
    }
  }
  @-webkit-keyframes successAnimation {
      to {
          left: 0px;
          width: 5px;
          visibility:hidden;
      }
  }

  .visaRequired.number-holder{
      p::after{
        display: block;
        position: absolute;
        right: 0;
        top: 0;
        content: ' ';
        width: 0;
        height: 0;
        border-style: solid;
        border-width: 0px 12px 12px 0;
        border-color: transparent #935ba6 transparent transparent;

    }
  }


`;

const BorderBox = styled(Box)`
  border-left: 1px solid #ebf1f7;
`;

const StyledGuestInput = styled(GuestInput)`
  padding: 15px 20px;
  font-size: 14px;

  &:hover {
    background-color: #f0f5fa;
  }
  &:read-only, &:-moz-read-only{
    background-color: #fff !important;
  }
`;

const StyledGuestBirthdayInput = styled(GuestBirthdayInput)`
  font-size: 14px;

  &:hover {
    background-color: #f0f5fa;
  }
`;

const DotBTN = styled(DotButton)`
  font-weight: normal;
`;

const ExtraFlex = props => <Flex px={0} align="center" {...props} />;
const ExtraBox = styled(props => <Box flex="0 auto" {...props} />)`
  display: inline-block;
  vertical-align: middle;
  width: auto;
  margin: 5px;
`;
const ExtraBoxInput = styled(props => <Box flex="1 0" {...props} />)`
  display: inline-block;
  vertical-align: middle;
  width: auto;

  input:focus{
    box-shadow: none;
  }
`;
const ExtraLabel = styled(props => <Label f={0} {...props} />)`
  vertical-align: middle;
  text-align: center;
  background-color: #dae6f2;
  border-radius: 4px;
  padding: 4px 10px;
  cursor: pointer;
  margin: 0 2px;
  font-size: 10px;
  line-height: 16px;

  &.invalid{
    background-color: #e40623;
    color: white;
  }
`;

const validateBirthDate = (dateValue) => {
  const dateObject = moment(dateValue); // eslint-disable-line no-underscore-dangle
  const isValid = dateObject.isValid() && dateObject.isBetween(moment('1900-01-01'), moment(), 'day');

  return isValid;
};

const validateExpiryDate = (dateValue) => {
  const dateObject = moment(dateValue); // eslint-disable-line no-underscore-dangle
  const isValid = dateObject.isValid() && dateObject.isBetween(moment(), moment('2100-01-01'), 'day');
  return isValid;
};

const validateField = (value, field) => {
  switch (field) {
    case 'firstName':
    case 'lastName':
      return value.length <= 50;
    case 'email':
      return /^$|^[a-z0-9!#$%&'*+/=?^_`{|}~-]+(?:\.[a-z0-9!#$%&'*+/=?^_`{|}~-]+)*@(?:[a-z0-9](?:[a-z0-9-]*[a-z0-9])?\.)+[a-z0-9](?:[a-z0-9-]*[a-z0-9])?/.test(value);
    case 'loyaltyNumber':
      return !isNaN(value);
    case 'documentNumber':
      // Max-length 20 chars and a-zA-Z0-9  [A-Za-z0-9_]{1,15}
      // return value.length <= 20;
      return /^[A-Za-z0-9]{0,20}$/.test(value);
    case 'icePhoneNumber':
      // If entered, it must begin with "+" or "00" followed by numbers 0-9
      return /^\+\d+$|^00\d+$|^$/.test(value);
    default:
      return true;
  }
};

class GuestListEntryContent extends React.PureComponent {
  static propTypes = {
    id: PropTypes.string.isRequired,
    departureId: PropTypes.string.isRequired,
    widths: PropTypes.object,
    number: PropTypes.number,
    onToggleExpanded: PropTypes.func,
    visaRequired: PropTypes.bool,
    collectPassportInfo: PropTypes.bool,
    collectIcePhoneNumber: PropTypes.bool,
    collectLoyaltyNumber: PropTypes.bool,
    invalidPassportFields: PropTypes.array,
  };
  static defaultProps = {
    widths: {},
    number: 0,
    invalidPassportFields: [],
    onToggleExpanded: () => {},
  };

  render() {
    const locale = moment.localeData();
    const dateFormat = locale._longDateFormat.L; // eslint-disable-line no-underscore-dangle
    const dateTitleFormat = 'LL';

    const {
      id,
      departureId,
      widths,
      number,
      onToggleExpanded,
      visaRequired,
      collectPassportInfo,
      collectIcePhoneNumber,
      collectLoyaltyNumber,
      invalidPassportFields,
    } = this.props;

    return (
      <T
        text={[
          AppText.Gender,
          AppText.Age,
          AppText.Nationality,
          AppText.SpecialNeed,
          AppText.SpecialNeedPlaceholder,
          AppText.EmailLabel,
          AppText.EmailPlaceholder,
          AppText.ExtraLabel,
          AppText.ExtraPlaceholder,
          AppText.DocumentTypeLabel,
          AppText.DocumentTypePlaceholder,
          AppText.DocumentNumberLabel,
          AppText.DocumentNumberPlaceholder,
          AppText.DocumentIssuingCountry,
          AppText.DocumentIssuingCountryPlaceholder,
          AppText.DocumentExpiryDate,
          AppText.DropdownFilterNoResults,
          AppText.DropdownClearValueDescription,
          AppText.GuestNeedVisa,
          AppText.IcePhoneNumberLabel,
          AppText.IcePhoneNumberPlaceholder,
        ]}
        render={([
          gender,
          age,
          nationality,
          specialNeed,
          specialNeedPlaceholder,
          emailLabel,
          emailPlaceholder,
          extraLabel,
          extraPlaceholder,
          documentTypeLabel,
          documentTypePlaceholder,
          documentNumberLabel,
          documentNumberPlaceholder,
          documentIssuingCountry,
          documentIssuingCountryPlaceholder,
          documentExpiryDate,
          dropdownFilterNoResults,
          dropdownClearValueText,
          guestNeedVisa,
          icePhoneNumberLabel,
          icePhoneNumberPlaceholder,
        ]) => (
          <StyleDiv className="passenger-row" id={`${departureId}-${id}`}>
            <GuestSaveStatus id={id} />
            <Flex align="center">
              <Box className={`number-holder ${visaRequired ? 'visaRequired' : ''}`} width={widths.number} title={visaRequired ? guestNeedVisa : ''}>
                <Text center>{number}</Text>
              </Box>
              { widths.firstName === 0 ? null : <BorderBox width={widths.firstName} px={0}>
                <StyledGuestInput guestId={id} field="firstName" departureId={departureId} trackFocus validate={validateField} />
              </BorderBox> }
              { widths.lastName === 0 ? null : <BorderBox width={widths.lastName} px={0}>
                <StyledGuestInput guestId={id} field="lastName" departureId={departureId} trackFocus validate={validateField} />
              </BorderBox> }
              { widths.birthday === 0 ? null : <BorderBox width={widths.birthday} px={0}>
                <StyledGuestBirthdayInput
                  guestId={id}
                  field="dateOfBirth"
                  departureId={departureId}
                  placeholder={dateFormat}
                  dateFormat={dateFormat}
                  trackFocus
                  validate={validateBirthDate}
                  title={value => moment(value, dateFormat).isValid() ? moment(value, dateFormat).format(dateTitleFormat) : null}
                  mapValue={{
                    in: storeValue => storeValue ? moment(storeValue).format(dateFormat) : '',
                    out: value => value ? moment(value, dateFormat).format(moment.HTML5_FMT.DATE) : null,
                  }}
                />
              </BorderBox> }
              { widths.gender === 0 ? null : <BorderBox width={widths.gender} px={0}>
                <GuestDropdown guestId={id} defaultText={gender} field="gender" />
              </BorderBox> }
              { widths.age === 0 ? null : <BorderBox width={widths.age} px={0}>
                <GuestDropdown guestId={id} defaultText={age} field="customerCategory" departureId={departureId} />
              </BorderBox> }
              { widths.nationality === 0 ? null : <BorderBox width={widths.nationality} px={0}>
                <GuestDropdown guestId={id} defaultText={nationality} noResultsText={dropdownFilterNoResults} field="nationality" searchable />
              </BorderBox> }
              { widths.cabin === 0 ? null : <BorderBox width={widths.cabin} px={0} className="cabin">
                <GuestCabin py={2} px={3} guestId={id} departureId={departureId} />
              </BorderBox> }
              <BorderBox width={widths.expand} px={0} className="extra-column">
                <DotBTN width={1} onClick={onToggleExpanded} />
              </BorderBox>
            </Flex>

            <div className={`expanded-row ${(collectPassportInfo ? ' with-passport-info' : '')}`}>
              <GuestListEntryExpanded id={id} className="expanded-row-content">
                <Box width={widths.space} />
                <BorderBox width={widths.email} py={1}>
                  <ExtraFlex>
                    <ExtraBox>
                      <ExtraLabel p={1} htmlFor={`epost-${id}0`}>{emailLabel}</ExtraLabel>
                    </ExtraBox>
                    <ExtraBoxInput>
                      <GuestInput guestId={id} py={1} htmlId={`epost-${id}0`} field="email" placeholder={emailPlaceholder} type="email" departureId={departureId} nonRequired validate={validateField} />
                    </ExtraBoxInput>
                  </ExtraFlex>
                </BorderBox>
                <BorderBox width={widths.specialNeeds}>
                  <ExtraFlex>
                    <ExtraBox>
                      <ExtraLabel p={1} htmlFor={`disabilityCode-${id}`}>{specialNeed}</ExtraLabel>
                    </ExtraBox>
                    <ExtraBoxInput>
                      <GuestDropdown py={2} guestId={id} field="disabilityCode" defaultText={specialNeedPlaceholder} clearable clearValueText={dropdownClearValueText} nullable departureId={departureId} nonRequired />
                    </ExtraBoxInput>
                  </ExtraFlex>
                </BorderBox>
                {collectLoyaltyNumber &&
                <BorderBox width={widths.extra} py={1}>
                  <ExtraFlex>
                    <ExtraBox>
                      <ExtraLabel p={1} htmlFor={`extra-${id}`}>{extraLabel}</ExtraLabel>
                    </ExtraBox>
                    <ExtraBoxInput>
                      <GuestInput guestId={id} py={1} field="loyaltyNumber" htmlId={`extra-${id}`} placeholder={extraPlaceholder} departureId={departureId} nonRequired validate={validateField} />
                    </ExtraBoxInput>
                  </ExtraFlex>
                </BorderBox>
                }
                {collectIcePhoneNumber === true &&
                  <BorderBox width={widths.icePhoneNumber} py={1}>
                    <ExtraFlex>
                      <ExtraBox>
                        <ExtraLabel p={1} htmlFor={`icePhoneNumber-${id}`}>{icePhoneNumberLabel}</ExtraLabel>
                      </ExtraBox>
                      <ExtraBoxInput>
                        <GuestInput guestId={id} py={1} field="icePhoneNumber" htmlId={`icePhoneNumber-${id}`} placeholder={icePhoneNumberPlaceholder} departureId={departureId} nonRequired validate={validateField} />
                      </ExtraBoxInput>
                    </ExtraFlex>
                  </BorderBox>
                }
              </GuestListEntryExpanded>
              {collectPassportInfo === true &&
                <GuestListEntryExpanded id={id} className="expanded-row-content">
                  <Box width={widths.space} />
                  <BorderBox width={widths.documentType}>
                    <ExtraFlex>
                      <ExtraBox>
                        <ExtraLabel className={invalidPassportFields.some(item => item.field === 'documentType') ? 'invalid' : ''} p={1} htmlFor={`documentType-${id}`}>{documentTypeLabel}</ExtraLabel>
                      </ExtraBox>
                      <ExtraBoxInput>
                        <GuestDropdown
                          py={2}
                          guestId={id}
                          htmlId={`documentType-${id}`}
                          field="documentType"
                          defaultText={documentTypePlaceholder}
                          clearable
                          clearValueText={dropdownClearValueText}
                          nullable
                          nonRequired
                          departureId={departureId}
                        />
                      </ExtraBoxInput>
                    </ExtraFlex>
                  </BorderBox>
                  <BorderBox width={widths.documentNumber} py={1}>
                    <ExtraFlex>
                      <ExtraBox>
                        <ExtraLabel className={invalidPassportFields.some(item => item.field === 'documentNumber') ? 'invalid' : ''} p={1} htmlFor={`documentNumber-${id}0`}>{documentNumberLabel}</ExtraLabel>
                      </ExtraBox>
                      <ExtraBoxInput>
                        <GuestInput
                          guestId={id}
                          py={1}
                          htmlId={`documentNumber-${id}0`}
                          field="documentNumber"
                          placeholder={documentNumberPlaceholder}
                          type="text"
                          nonRequired
                          departureId={departureId}
                          validate={validateField}
                        />
                      </ExtraBoxInput>
                    </ExtraFlex>
                  </BorderBox>
                  <BorderBox width={widths.documentIssuingCountry}>
                    <ExtraFlex>
                      <ExtraBox>
                        <ExtraLabel className={invalidPassportFields.some(item => item.field === 'documentIssuingCountry') ? 'invalid' : ''} p={1} htmlFor={`documentIssuingCountry-${id}`}>{documentIssuingCountry}</ExtraLabel>
                      </ExtraBox>
                      <ExtraBoxInput>
                        <GuestDropdown
                          py={2}
                          guestId={id}
                          field="documentIssuingCountry"
                          defaultText={documentIssuingCountryPlaceholder}
                          clearable
                          clearValueText={dropdownClearValueText}
                          nullable
                          nonRequired
                          departureId={departureId}
                          searchable
                        />
                      </ExtraBoxInput>
                    </ExtraFlex>
                  </BorderBox>
                  <BorderBox width={widths.documentExpiryDate} px={0}>
                    <ExtraFlex>
                      <ExtraBox>
                        <ExtraLabel className={invalidPassportFields.some(item => item.field === 'documentExpiryDate') ? 'invalid' : ''} p={1} htmlFor={`documentExpiryDate-${id}`}>{documentExpiryDate}</ExtraLabel>
                      </ExtraBox>
                      <ExtraBoxInput>
                        <StyledGuestBirthdayInput
                          guestId={id}
                          py={1}
                          field="documentExpiryDate"
                          departureId={departureId}
                          placeholder={dateFormat}
                          dateFormat={dateFormat}
                          trackFocus
                          nonRequired
                          canBeNull
                          validate={validateExpiryDate}
                          title={value => moment(value, dateFormat).isValid() ? moment(value, dateFormat).format(dateTitleFormat) : null}
                          mapValue={{
                            in: storeValue => storeValue ? moment(storeValue).format(dateFormat) : '',
                            out: value => value ? moment(value, dateFormat).format(moment.HTML5_FMT.DATE) : null,
                          }}
                        />
                      </ExtraBoxInput>
                    </ExtraFlex>
                  </BorderBox>
                </GuestListEntryExpanded>
              }
            </div>
          </StyleDiv>
        )}
      />
    );
  }
}

export default GuestListEntryContent;



// WEBPACK FOOTER //
// ./src/components/organisms/GuestListEntryContent/index.js